import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input, InputNumber, Select, Modal, message } from "antd";
import {
  SearchOutlined, PlusOutlined, DeleteOutlined,
  PrinterOutlined, TeamOutlined, DashboardOutlined, PoweroffOutlined,
  ShopOutlined, SunOutlined, MoonOutlined, UserOutlined,
  ShoppingCartOutlined, UnorderedListOutlined, FileTextOutlined, RollbackOutlined,
  CloudSyncOutlined, WifiOutlined, DisconnectOutlined, WalletOutlined,
} from "@ant-design/icons";
import i18nModule from "../../../i18";
import Cookies from "js-cookie";
import {
  useProductsCatalog,
  useCustomersCatalog,
  invalidateCatalog,
} from "../../../hooks/useCatalogData";
import { apiClient, getStorageUrl } from "../../../shared/api/AllApi";
import { getApiErrorMessage } from "../../../shared/api/errors";
import { parseLocalizedName } from "../../../utils/localization";
import { formatCurrency, roundToIqdDenomination } from "../../../utils/formatters";
import { formatOrderNo, orderDocNo } from "../../../shared/lib/documentNumbers";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { printHtml } from "../../../shared/lib/printHtml";
import usePermissions from "../../../hooks/usePermissions";
import useUserShift from "../../../hooks/useUserShift";
import { isBrowserOnline, isNetworkError } from "../../../shared/lib/connectivity";
import useOfflineSaleSync from "../hooks/useOfflineSaleSync";
import { enqueueOfflineSale } from "../lib/offlineSaleQueue";
import { loadCatalogCache, saveCatalogCache } from "../lib/posCatalogCache";
import PosSubPeople from "../components/PosSubPeople";
import PosSubProducts from "../components/PosSubProducts";
import PosSubOrders from "../components/PosSubOrders";
import PosSubReturns from "../components/PosSubReturns";
import PosSubBatches from "../components/PosSubBatches";
import PosSubStatement from "../components/PosSubStatement";
import PosSubDrafts, { saveDraft, deleteDraft, loadDrafts } from "../components/PosSubDrafts";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import "../../../shared/styles/PosTerminal.css";

/** Business-day date string (YYYY-MM-DD), respecting settings cutoff. */
const currentBusinessDateStr = () => {
  const [h, m] = String(readCachedSystemConfig()?.business_day_start || "00:00").split(":");
  const cutoffMin = (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
  return dayjs().subtract(cutoffMin, "minute").format("YYYY-MM-DD");
};

const toArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.data?.data)) return raw.data.data;
  if (raw && typeof raw === "object") {
    const found = Object.values(raw).find((v) => Array.isArray(v));
    if (found) return found;
  }
  return [];
};

const PosTerminal = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission, canSeeAll } = usePermissions();
  const { locked: shiftLocked, hoursLabel: shiftHours } = useUserShift();
  const canSeeAllRegisterData = canSeeAll("daily_register");
  const canReturn = hasPermission("pos.return");
  // Full customer CRUD in POS; separate Customers dashboard pages stay hidden
  const canCustomers = hasPermission("customers.index");
  const canAddCustomer = hasPermission("customers.add");
  const canEditCustomer = hasPermission("customers.edit");
  const canDeleteCustomer = hasPermission("customers.delete");
  const canFarmers = hasPermission("vendors.index");
  const canAddFarmer = hasPermission("vendors.add");
  const canEditFarmer = hasPermission("vendors.edit");
  const canDeleteFarmer = hasPermission("vendors.delete");
  const canQuickAdd = canAddCustomer || canAddFarmer;
  const [subView, setSubView] = useState(null);
  const [subViewParams, setSubViewParams] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("pos-dark") === "1");
  const [posLang, setPosLang] = useState(() =>
    i18nModule.normalizeLang(Cookies.get("language", "Language") || localStorage.getItem("selectedLanguage") || "en")
  );

  const handleLangChange = useCallback((lang) => {
    const next = i18nModule.normalizeLang(lang);
    setPosLang(next);
    // Centralized in i18.js: waits for locale JSON, updates dir + strings together.
    i18nModule.changeLanguage(next);
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("pos-dark");
      localStorage.setItem("pos-dark", "1");
    } else {
      document.documentElement.classList.remove("pos-dark");
      localStorage.setItem("pos-dark", "0");
    }
  }, [darkMode]);

  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  // Who pays commission is a global setting now (System Settings → POS), not a
  // per-sale toggle: "customer" adds it on top of the subtotal; "farmer" means
  // the customer pays exactly the subtotal (commission comes out of the farmer's
  // payout instead, computed server-side).
  const isCustomerSale = (systemConfig?.pos_commission_payer || "customer") === "customer";
  const draftsEnabled = systemConfig?.pos_enable_drafts !== false;
  const [, setCurrentTime] = useState("");
  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sessionUser")) || {}; } catch { return {}; }
  });

  // Server-tracked register session. Each open/close is its own row with a
  // unique id; runs on a daily cycle automatically (a stale register rolls over).
  const [register, setRegister] = useState(null);
  const registerOpen = register?.status === "open";
  const sessionStart = register?.opened_at || null;

  const loadRegister = useCallback(async () => {
    try {
      const r = await apiClient.get("/pos/register/current");
      setRegister(r.data || null);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadRegister(); }, [loadRegister]);

  const [showSessionPanel, setShowSessionPanel] = useState(false);
  const [sessionSales, setSessionSales] = useState(null);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);

  useEffect(() => {
    if (!showSessionPanel) return;
    const today = new Date().toISOString().slice(0, 10);
    apiClient.get("/pos/daily-register", { params: { date: today } })
      .then((r) => setSessionSales(r.data))
      .catch(() => setSessionSales(null));
    loadRegister();
  }, [showSessionPanel]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tick = () =>
      setCurrentTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetchSystemConfig().then(setSystemConfig).catch(() => { });
    // Settings (commission mode/rate, etc.) may change in another tab/session while
    // this POS screen stays open — refresh whenever the tab regains focus/visibility
    // so a stale cached config never silently drives the live cart total.
    const refresh = () => {
      if (document.visibilityState === "visible") {
        fetchSystemConfig().then(setSystemConfig).catch(() => { });
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  const mainCurrency = systemConfig?.currency_code || "IQD";

  const { data: products, loading: productsLoading, refresh: refreshProducts } = useProductsCatalog();
  const { data: customers, refresh: refreshCustomers } = useCustomersCatalog();
  const { online: isOnline, pendingCount: offlinePendingCount, runSync } = useOfflineSaleSync({ enabled: true, t });

  const [ordersCount, setOrdersCount] = useState(0);
  const [draftsCount, setDraftsCount] = useState(0);
  useEffect(() => {
    if (subView) return;
    const today = currentBusinessDateStr();
    apiClient.get("/pos/sales", { params: { date_from: today, date_to: today } })
      .then((r) => setOrdersCount(Array.isArray(r.data) ? r.data.length : 0))
      .catch(() => { });
    if (!draftsEnabled) {
      setDraftsCount(0);
      return;
    }
    loadDrafts()
      .then((list) => setDraftsCount(list.length))
      .catch(() => { });
  }, [subView, draftsEnabled]);

  // Products this cashier is allowed to sell. `null` = not loaded yet (so we don't
  // flash the full catalog before the restriction is known); once loaded, an empty
  // array means "no restriction / all products".
  const [allowedProductIds, setAllowedProductIds] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get("/pos/my-products");
        const ids = Array.isArray(r.data?.product_ids) ? r.data.product_ids : [];
        if (!cancelled) setAllowedProductIds(ids);
        saveCatalogCache("my-products", ids);
      } catch (err) {
        if (!isNetworkError(err)) {
          if (!cancelled) setAllowedProductIds([]);
          return;
        }
        const cached = await loadCatalogCache("my-products");
        if (!cancelled) {
          setAllowedProductIds(Array.isArray(cached?.data) ? cached.data : []);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [vendors, setVendors] = useState([]);
  const loadVendors = useCallback(async () => {
    try {
      const r = await apiClient.get("/vendors");
      const list = toArray(r.data);
      setVendors(list);
      saveCatalogCache("vendors", list);
    } catch (err) {
      if (!isNetworkError(err)) {
        setVendors([]);
        return;
      }
      const cached = await loadCatalogCache("vendors");
      setVendors(Array.isArray(cached?.data) ? cached.data : []);
    }
  }, []);
  useEffect(() => { loadVendors(); }, [loadVendors]);

  const [search, setSearch] = useState("");
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pos-cart") || "[]"); } catch { return []; }
  });
  // Commission % comes from settings — not editable per sale
  const retainPercent = parseFloat(systemConfig?.pos_commission_percent || 0);
  const commissionMode = systemConfig?.pos_commission_mode || "percent";
  const perQtyAmount = parseFloat(systemConfig?.pos_commission_per_qty_amount || 0);
  const customPercent = null;
  const [paymentMethod, setPaymentMethod] = useState(
    () => localStorage.getItem("pos-payment") || "cash"
  );
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [cashCustomerName, setCashCustomerName] = useState("");
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddType, setQuickAddType] = useState("customer"); // customer | farmer
  const [quickAddName, setQuickAddName] = useState("");
  const [quickAddPhone, setQuickAddPhone] = useState("");
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const [defaultFarmerId, setDefaultFarmerId] = useState(null);
  const [mobileTab, setMobileTab] = useState("products"); // "products" | "cart" — mobile only

  // The hidden "walk-in / default POS" customer — used behind the scenes for
  // cash sales when the cashier hasn't picked anyone. It is never shown in the
  // dropdown and never pre-selected.
  const defaultPosCustomerId = useMemo(
    () => customers?.find((c) => c.is_pos_default)?.id || null,
    [customers]
  );

  // After a sale, clear the picker back to empty (not the default customer).
  const resetToDefaultCustomer = useCallback(() => {
    setSelectedCustomer(null);
  }, []);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("pos-cart", JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    localStorage.setItem("pos-payment", paymentMethod);
  }, [paymentMethod]);
  const [saleToPrint, setSaleToPrint] = useState(null);
  const printTimerRef = useRef(null);

  // Android sends a rendered receipt image to RawBT. Every other platform keeps
  // using the existing browser print path (including Windows kiosk printing).
  const printReceipt = useCallback(async () => {
    const node = document.getElementById("pos-receipt-print");
    if (!node) { window.print(); return; }

    if (/Android/i.test(navigator.userAgent)) {
      try {
        if (document.fonts?.ready) await document.fonts.ready;
        const canvas = await html2canvas(node, {
          backgroundColor: "#ffffff",
          scale: 2,
          useCORS: true,
          logging: false,
          scrollX: 0,
          scrollY: 0,
          onclone: (clonedDoc) => {
            const clonedReceipt = clonedDoc.getElementById("pos-receipt-print");
            if (!clonedReceipt) return;
            clonedReceipt.style.position = "absolute";
            clonedReceipt.style.left = "0";
            clonedReceipt.style.top = "0";
          },
        });
        const base64 = canvas.toDataURL("image/png").split(",")[1];
        window.location.href = `rawbt:data:image/png;base64,${base64}`;
        return;
      } catch (error) {
        console.error("RawBT receipt printing failed:", error);
        message.error(t("posRawBtPrintFailed", "RawBT printing failed. Opening Android print instead."));
      }
    }

    printHtml(
      '<!doctype html><html><head><meta charset="utf-8"><style>' +
      '@page{size:80mm auto;margin:0}' +
      'html,body{margin:0;padding:0;width:80mm;max-width:80mm}' +
      "body{font-family:Tahoma,'Segoe UI',Arial,sans-serif;font-size:16px;line-height:1.35;color:#000;background:#fff;" +
      '-webkit-print-color-adjust:exact;print-color-adjust:exact}' +
      '*{box-sizing:border-box}' +
      '.pos-receipt-page{width:80mm;max-width:80mm;page-break-after:always;break-after:page}' +
      '.pos-receipt-page:last-child{page-break-after:auto;break-after:auto}' +
      '</style></head><body>' + node.innerHTML + '</body></html>'
    );
  }, [t]);

  // Auto-print immediately after checkout (not for manual draft prints)
  useEffect(() => {
    if (!saleToPrint || saleToPrint._draft) return;
    printTimerRef.current = setTimeout(() => {
      printReceipt();
      // A reprint of a past order must not clear the current cart.
      if (!saleToPrint._reprint) {
        setCart([]);
        setMobileTab("products");
        resetToDefaultCustomer();
        setCashCustomerName("");
        localStorage.removeItem("pos-cart");
        localStorage.removeItem("pos-payment");
      }
    }, 80);
    return () => clearTimeout(printTimerRef.current);
  }, [saleToPrint]); // eslint-disable-line react-hooks/exhaustive-deps


  const filteredProducts = useMemo(() => {
    // Not loaded yet — show nothing so restricted products never flash on screen.
    if (allowedProductIds === null) return [];
    const term = search.trim().toLowerCase();
    const allowedSet = allowedProductIds.length ? new Set(allowedProductIds.map(Number)) : null;
    return products
      .filter((p) => !allowedSet || allowedSet.has(Number(p.id)))
      .map((p) => ({
        id: p.id,
        name: parseLocalizedName(p.name, i18n.language, "--"),
        price: Number(p.price) || 0,
        image: p.image_url || null,
        sku: p.sku || "",
        unitId: p.unit_id || null,
        sortOrder: Number(p.sort_order) || 0,
      }))
      .filter((p) => {
        if (!term) return true;
        return (
          p.name.toLowerCase().includes(term) ||
          String(p.sku).toLowerCase().includes(term)
        );
      })
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  }, [products, search, i18n.language, allowedProductIds]);

  const customerOptions = useMemo(
    () => customers
      .filter((c) => !c.is_pos_default)
      .map((c) => ({ value: c.id, label: c.name || `Customer ${c.id}` })),
    [customers]
  );
  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.id, label: v.name || `Vendor ${v.id}` })),
    [vendors]
  );
  // Name lookups for the printed receipt (farmer + unit per line).
  const vendorNameById = useMemo(() => {
    const m = {}; vendors.forEach((v) => { m[v.id] = v.name || `Vendor ${v.id}`; }); return m;
  }, [vendors]);

  const addToCart = useCallback((product) => {
    setCart((prev) => {
      const ex = prev.find((l) => l.id === product.id);
      if (ex) return prev;
      return [...prev, {
        ...product,
        qty: null,
        weight: null,
        price: null,
        vendorId: defaultFarmerId || null,
        unitId: product.unitId || null,
      }];
    });
  }, [defaultFarmerId]);

  const openQuickAdd = useCallback((type = "customer") => {
    const next =
      type === "farmer" && canAddFarmer
        ? "farmer"
        : canAddCustomer
          ? "customer"
          : canAddFarmer
            ? "farmer"
            : null;
    if (!next) return;
    setQuickAddType(next);
    setQuickAddName("");
    setQuickAddPhone("");
    setQuickAddOpen(true);
  }, [canAddCustomer, canAddFarmer]);

  const saveQuickAdd = useCallback(async () => {
    const name = quickAddName.trim();
    if (!name) {
      message.warning(t("nameRequired", "Name is required"));
      return;
    }
    setQuickAddSaving(true);
    try {
      if (quickAddType === "customer") {
        const res = await apiClient.post("/customers", {
          name,
          phone_number: quickAddPhone.trim() || "",
        });
        invalidateCatalog("customers");
        await refreshCustomers();
        const newId = res.data?.id;
        if (newId) {
          setSelectedCustomer(newId);
        }
        message.success(t("posQuickCustomerAdded", "Customer added"));
      } else {
        const res = await apiClient.post("/vendors", {
          name,
          phone: quickAddPhone.trim() || "",
        });
        await loadVendors();
        const newId = res.data?.id;
        if (newId) {
          setDefaultFarmerId(newId);
          setCart((prev) =>
            prev.map((l) => (l.vendorId ? l : { ...l, vendorId: newId }))
          );
        }
        message.success(t("posQuickFarmerAdded", "Farmer added"));
      }
      setQuickAddOpen(false);
    } catch (e) {
      message.error(getApiErrorMessage(e, t("posQuickAddFailed", "Failed to save"), t));
    } finally {
      setQuickAddSaving(false);
    }
  }, [quickAddName, quickAddPhone, quickAddType, t, refreshCustomers, loadVendors]);

  const setQtyDirect = useCallback((id, value) => {
    setCart((prev) =>
      prev.map((l) => l.id === id ? { ...l, qty: value === null || value === '' ? null : Number(value) } : l)
    );
  }, []);

  const setWeightForLine = useCallback((id, weight) => {
    setCart((prev) => prev.map((l) => l.id === id ? { ...l, weight: weight === null || weight === '' ? null : Math.max(0, Number(weight) || 0) } : l));
  }, []);

  const setVendorForLine = useCallback((id, vendorId) => {
    setCart((prev) => prev.map((l) => l.id === id ? { ...l, vendorId } : l));
  }, []);

  const setUnitForLine = useCallback((id, unitId) => {
    setCart((prev) => prev.map((l) => l.id === id ? { ...l, unitId } : l));
  }, []);

  const setPriceForLine = useCallback((id, price) => {
    setCart((prev) => prev.map((l) => l.id === id ? { ...l, price: price === null || price === undefined ? null : Math.max(0, Number(price) || 0) } : l));
  }, []);

  const removeLine = useCallback((id) => {
    setCart((prev) => prev.filter((l) => l.id !== id));
  }, []);

  // Line amounts stay exact; only the final grand total snaps to cash denomination (system settings).
  const lineSubtotal = (l) => (l.price || 0) * (l.weight > 0 ? l.weight : (l.qty || 0));
  const cartTotal = useMemo(() => cart.reduce((s, l) => s + lineSubtotal(l), 0), [cart]); // eslint-disable-line react-hooks/exhaustive-deps
  const cartQty = useMemo(() => cart.reduce((s, l) => s + Math.max(0, l.qty || 0), 0), [cart]);
  const effectivePercent = customPercent !== null ? customPercent : retainPercent;
  const retainedAmount = useMemo(() => {
    if (!isCustomerSale) return 0;
    const pctPart = commissionMode === "percent" || commissionMode === "mix"
      ? (cartTotal * (effectivePercent || 0)) / 100
      : 0;
    const perQtyPart = commissionMode === "per_qty" || commissionMode === "mix"
      ? cartQty * (perQtyAmount || 0)
      : 0;
    return pctPart + perQtyPart;
  }, [cartTotal, cartQty, effectivePercent, isCustomerSale, commissionMode, perQtyAmount]);
  const grandTotal = roundToIqdDenomination(cartTotal + retainedAmount);

  const handlePrint = useCallback(() => {
    if (!saleToPrint && cart.length > 0) {
      const pct = isCustomerSale
        ? (customPercent !== null ? customPercent : retainPercent)
        : 0;
      const draftTotal = cart.reduce((s, l) => s + lineSubtotal(l), 0);
      const draftQty = cart.reduce((s, l) => s + Math.max(0, l.qty || 0), 0);
      const draftPctPart = (commissionMode === "percent" || commissionMode === "mix")
        ? (draftTotal * (pct || 0)) / 100
        : 0;
      const draftPerQtyPart = (commissionMode === "per_qty" || commissionMode === "mix")
        ? draftQty * (perQtyAmount || 0)
        : 0;
      const draftRetained = isCustomerSale ? (draftPctPart + draftPerQtyPart) : 0;
      const draft = {
        items: cart,
        total: draftTotal,
        percent: pct || 0,
        mode: commissionMode,
        perQtyAmount,
        retained: draftRetained,
        taxed: isCustomerSale,
        paymentMethod,
        customerLabel: paymentMethod === "cash" ? cashCustomerName.trim() || null : null,
        date: new Date(),
        _draft: true,
      };
      setSaleToPrint(draft);
      setTimeout(() => {
        printReceipt();
        setSaleToPrint(null);
      }, 120);
    } else {
      printReceipt();
    }
  }, [saleToPrint, cart, retainPercent, paymentMethod, printReceipt, isCustomerSale, cashCustomerName]);

  const [checkingOut, setCheckingOut] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (!draftsEnabled && subView === "drafts") setSubView(null);
  }, [draftsEnabled, subView]);

  useEffect(() => {
    if (!canCustomers && paymentMethod === "credit") setPaymentMethod("cash");
  }, [canCustomers, paymentMethod]);
  useEffect(() => {
    if (paymentMethod === "cash") {
      setValidationErrors((e) => (e.customer ? (() => { const n = { ...e }; delete n.customer; return n; })() : e));
    }
  }, [paymentMethod]);

  const handleSaveDraft = useCallback(async () => {
    if (cart.length === 0) { message.warning(t("posTerminalCartEmptyWarn", "Cart is empty")); return; }
    const customerLabel = selectedCustomer
      ? (customers.find((c) => c.id === selectedCustomer)?.name || null)
      : null;
    try {
      await saveDraft({ cart, selectedCustomer, paymentMethod, customerLabel, taxed: isCustomerSale });
      setDraftsCount((c) => c + 1);
      setCart([]);
      resetToDefaultCustomer();
      localStorage.removeItem("pos-cart");
      message.success(t("posTerminalDraftSaved", "Draft saved — cart cleared"));
    } catch (e) {
      message.error(getApiErrorMessage(e, t("posTerminalDraftFailed", "Failed to save draft"), t));
    }
  }, [cart, selectedCustomer, paymentMethod, customers, isCustomerSale]);

  const confirmSale = async () => {
    if (cart.length === 0) return;
    if (shiftLocked) {
      message.error(
        t(
          "outsideShiftBlock",
          "Outside your working hours ({{hours}}). You cannot sell, return, edit, cancel, or delete right now.",
          { hours: shiftHours }
        )
      );
      return;
    }

    // Credit sales must go to a real customer (the hidden walk-in can't owe money).
    if (paymentMethod === "credit" && !selectedCustomer) {
      setValidationErrors((e) => ({ ...e, customer: true }));
      setMobileTab("cart");
      message.error(t("posCreditNeedsCustomer", "Please select a customer for credit sales"));
      return;
    }

    const errors = {};
    cart.forEach((line) => {
      if (!line.vendorId) errors[`vendor_${line.id}`] = true;
      if (!(line.qty > 0) && !(line.weight > 0)) errors[`qty_${line.id}`] = true;
    });
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setMobileTab("cart");
      message.error(
        Object.keys(errors).some((k) => k.startsWith("qty_"))
          ? (t("posTerminalQtyOrWeightRequired", "Enter qty and/or weight for each item"))
          : (t("posTerminalVendorRequired", "Please select a vendor for each item"))
      );
      return;
    }
    setValidationErrors({});
    setCheckingOut(true);
    let saleId = null;
    let saleDocumentNo = null;
    let pendingSync = false;
    // Cash walk-in: prefer selected customer, else flagged POS default.
    // Backend will also auto-create Walk-in if needed (orders.customer_id is NOT NULL).
    const saleCustomerId = selectedCustomer || defaultPosCustomerId || null;
    const customerLabel =
      paymentMethod === "cash" && cashCustomerName.trim()
        ? cashCustomerName.trim()
        : selectedCustomer
        ? (customerOptions.find((c) => c.value === selectedCustomer)?.label || null)
        : null;
    const salePayload = {
      customer_id: saleCustomerId,
      payment_method: paymentMethod,
      ...(paymentMethod === "cash" && cashCustomerName.trim()
        ? { pos_customer_name: cashCustomerName.trim() }
        : {}),
      // Customer sale = taxed (commission added); farmer sale = commission-free
      taxed: isCustomerSale,
      items: cart.map((line) => {
        const qty = line.qty > 0 ? Number(line.qty) : null;
        const weight = line.weight > 0 ? Number(line.weight) : null;
        // Billing unit for money/stock: prefer weight when present, else qty
        const billing = weight ?? qty ?? 0;
        return {
          product_id: line.id,
          quantity: billing,
          qty,
          unit_price: line.price || 0,
          vendor_id: line.vendorId || null,
          weight,
        };
      }),
    };
    const receiptSnapshot = {
      items: [...cart],
      total: cartTotal,
      percent: isCustomerSale ? (effectivePercent || 0) : 0,
      mode: commissionMode,
      perQtyAmount,
      retained: retainedAmount,
      taxed: isCustomerSale,
      paymentMethod,
      customerLabel,
      date: new Date(),
    };

    const queueSaleOffline = async () => {
      const queued = await enqueueOfflineSale({
        payload: salePayload,
        receipt: receiptSnapshot,
      });
      pendingSync = true;
      saleDocumentNo = `OFFLINE-${String(queued.clientSaleId).slice(0, 8).toUpperCase()}`;
      message.warning(
        t(
          "posOfflineSaleQueued",
          "No internet — sale saved on this device and will sync when back online (kept up to 24 hours)."
        )
      );
    };

    // Already offline → save locally only (do not hit the API first).
    if (!isBrowserOnline()) {
      try {
        await queueSaleOffline();
      } catch (queueErr) {
        message.error(
          getApiErrorMessage(queueErr, t("posTerminalSaveFailed", "Failed to save the sale"), t)
        );
        setCheckingOut(false);
        return;
      }
    } else {
      try {
        const res = await apiClient.post("/pos/sale", salePayload);
        saleId = res.data?.id ?? null;
        saleDocumentNo = res.data?.document_no || formatOrderNo(saleId, new Date());
        invalidateCatalog("customers");
      } catch (e) {
        // Went offline mid-request / no response → queue locally.
        if (isNetworkError(e) || !isBrowserOnline()) {
          try {
            await queueSaleOffline();
          } catch (queueErr) {
            message.error(
              getApiErrorMessage(queueErr, t("posTerminalSaveFailed", "Failed to save the sale"), t)
            );
            setCheckingOut(false);
            return;
          }
        } else {
          message.error(getApiErrorMessage(e, t("posTerminalSaveFailed", "Failed to save the sale"), t));
          setCheckingOut(false);
          return;
        }
      }
    }
    setCheckingOut(false);
    setOrdersCount((c) => c + 1);
    if (!pendingSync) {
      loadRegister();
    }
    else runSync({ silent: true });
    setSaleToPrint({
      id: saleId,
      documentNo: saleDocumentNo,
      ...receiptSnapshot,
      _pendingSync: pendingSync,
    });
  };

  const companyTitle =
    (systemConfig?.invoice_company_name || "").trim() || (systemConfig?.site_name || "").trim() || "";
  const tagline = (systemConfig?.invoice_tagline || "").trim();
  // POS slips use invoice logo only — never the sidebar logo (often a different brand).
  const headerLogoSrc = (systemConfig?.invoice_logo_url || "").trim();
  const displayEmail = (systemConfig?.invoice_email || "").trim();
  const displayWeb = (systemConfig?.invoice_website || "").trim();
  const phoneLines = (systemConfig?.invoice_phones || "").trim()
    ? systemConfig.invoice_phones.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
    : [];
  const footerNote = (systemConfig?.invoice_footer_note || "").trim();
  const addressText = (systemConfig?.invoice_address || "").trim();
  const userName = user?.name || user?.username || t("user", "User");

  // Theme tokens for JS-rendered popups (settings / register) so they
  // match the CSS-variable theme in both light and dark mode.
  const pt = darkMode
    ? {
      bg: "#1a2740", raised: "#30445c", border: "#3d5169", text: "#f8fafc", mute: "#aab7ca", faint: "#7688a0",
      accent: "#60a5fa", accentSoft: "#2b4f7a", green: "#4ade80", greenSoft: "rgba(34,197,94,0.14)",
      amber: "#fbbf24", amberSoft: "rgba(245,158,11,0.14)", danger: "#f87171", dangerSoft: "rgba(248,113,113,0.14)"
    }
    : {
      bg: "#ffffff", raised: "#f8fafc", border: "#e5e9f0", text: "#0f172a", mute: "#475569", faint: "#94a3b8",
      accent: "#2563eb", accentSoft: "#eff6ff", green: "#16a34a", greenSoft: "#f0fdf4",
      amber: "#d97706", amberSoft: "#fffbeb", danger: "#dc2626", dangerSoft: "#fef2f2"
    };

  if (subView === "people") {
    return (
      <PosSubPeople
        onBack={() => { setSubView(null); refreshCustomers(); }}
        vendors={vendors}
        onVendorMutated={loadVendors}
        canCustomers={canCustomers}
        canFarmers={canFarmers}
        canAddCustomer={canAddCustomer}
        canAddFarmer={canAddFarmer}
        canEditCustomer={canEditCustomer}
        canDeleteCustomer={canDeleteCustomer}
        canEditFarmer={canEditFarmer}
        canDeleteFarmer={canDeleteFarmer}
        onViewClientStatement={(id, name) => { setSubViewParams({ id, name }); setSubView("client-statement"); }}
        onViewVendorStatement={(id, name) => { setSubViewParams({ id, name }); setSubView("vendor-statement"); }}
      />
    );
  }
  if (subView === "products") {
    return (
      <PosSubProducts
        onBack={() => setSubView(null)}
        onMutated={refreshProducts}
      />
    );
  }
  if (subView === "drafts") {
    if (!draftsEnabled) return null;
    return (
      <PosSubDrafts
        onBack={() => setSubView(null)}
        onResume={async (draft) => {
          setCart(draft.cart);
          setSelectedCustomer(draft.selectedCustomer || null);
          setPaymentMethod(draft.paymentMethod || "cash");
          try { await deleteDraft(draft.id); setDraftsCount((c) => Math.max(0, c - 1)); } catch { }
          setSubView(null);
          message.success(t("posTerminalDraftLoaded", "Draft loaded into cart"));
        }}
      />
    );
  }
  if (subView === "returns") {
    return <PosSubReturns onBack={() => setSubView(null)} vendors={vendors} />;
  }
  if (subView === "batches") {
    return <PosSubBatches onBack={() => setSubView(null)} vendors={vendors} products={products} />;
  }
  if (subView === "orders") {
    return (
      <PosSubOrders
        onBack={() => setSubView(null)}
        onReprint={(saleData) => {
          setSubView(null);
          if (saleData?._pendingSync && saleData.receipt) {
            setSaleToPrint({
              _reprint: true,
              _pendingSync: true,
              id: null,
              documentNo: saleData.document_no,
              items: saleData.receipt.items || [],
              total: saleData.receipt.total ?? saleData.total_price ?? 0,
              percent: saleData.receipt.percent ?? 0,
              mode: saleData.receipt.mode || "percent",
              perQtyAmount: saleData.receipt.perQtyAmount ?? 0,
              retained: saleData.receipt.retained ?? 0,
              taxed: saleData.receipt.taxed !== false,
              paymentMethod: saleData.receipt.paymentMethod || saleData.payment_method,
              customerLabel: saleData.receipt.customerLabel || saleData.customer?.name || null,
              date: new Date(saleData.receipt.date || saleData.created_at),
            });
            return;
          }
          // Reconstruct saleToPrint from the order detail (order_products / single_price / total_price).
          // commission_mode/commission_per_qty_amount are stamped on the order at sale time — for
          // orders created before that snapshot existed, they're NULL and we fall back to 'percent'
          // + retain_percent, exactly matching this feature's pre-existing behavior.
          setSaleToPrint({
            _reprint: true,
            id: saleData.id,
            documentNo: saleData.document_no || formatOrderNo(saleData.id, saleData.created_at),
            items: (saleData.order_products || saleData.items || []).map((item) => ({
              id: item.product_id,
              name: item.product
                ? parseLocalizedName(item.product.name, i18n.language, `#${item.product_id}`)
                : `Product #${item.product_id}`,
              qty: item.quantity,
              weight: item.weight,
              price: item.single_price ?? item.unit_price ?? 0,
              vendorId: item.vendor_id || null,
              unitId: item.product?.unit_id || null,
              subtotal: item.total_price,
            })),
            total: saleData.total_price ?? saleData.total ?? 0,
            percent: saleData.is_taxed === false || saleData.is_taxed === 0
              ? 0
              : (saleData.commission_percent ?? saleData.retain_percent ?? retainPercent ?? 0),
            mode: saleData.commission_mode || "percent",
            perQtyAmount: parseFloat(saleData.commission_per_qty_amount || 0),
            retained: saleData.is_taxed === false || saleData.is_taxed === 0
              ? 0
              : (saleData.retained_amount ?? saleData.commission_amount ?? 0),
            taxed: !(saleData.is_taxed === false || saleData.is_taxed === 0),
            paymentMethod: saleData.payment_method,
            customerLabel: saleData.pos_customer_name || saleData.customer?.name || null,
            date: new Date(saleData.created_at),
          });
        }}
      />
    );
  }
  if (subView === "client-statement" || subView === "vendor-statement") {
    return (
      <PosSubStatement
        type={subView === "client-statement" ? "client" : "vendor"}
        entityId={subViewParams?.id}
        entityName={subViewParams?.name}
        onBack={() => { setSubView(null); setSubViewParams(null); }}
      />
    );
  }

  const bottomNav = [
    { label: t("posNavOrders", "Orders"), icon: <UnorderedListOutlined />, action: () => setSubView("orders") },
    { label: t("posNavProducts", "Products"), icon: <ShopOutlined />, action: () => setSubView("products") },
    // { label: "People", icon: <TeamOutlined />, action: () => setSubView("people") },
    { label: t("exitPos", "Exit"), icon: <DashboardOutlined />, action: () => navigate("/orders") },
    {
      label: darkMode ? t("lightMode", "Light Mode") : t("darkMode", "Dark Mode"),
      icon: darkMode ? <SunOutlined /> : <MoonOutlined />,
      action: () => setDarkMode((d) => !d),
      className: "pos-nav-theme",
    },
  ];

  const cartItemCount = cart.length;
  const mobileStickyPrimary =
    mobileTab === "products"
      ? {
        label: t("posMobileCart", "Cart"),
        disabled: false,
        onClick: () => setMobileTab("cart"),
      }
      : {
        label: checkingOut ? t("posTerminalSaving", "Saving...") : t("posTerminalCheckout", "CHECKOUT"),
        disabled: cart.length === 0 || checkingOut || shiftLocked,
        onClick: confirmSale,
      };

  return (
    <>
      {shiftLocked && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            background: "#9f1239",
            color: "#fff",
            textAlign: "center",
            padding: "8px 12px",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {t(
            "outsideShiftBanner",
            "Outside working hours ({{hours}}). Selling, returns, edit, cancel and delete are blocked. You can still view data and log out.",
            { hours: shiftHours }
          )}
        </div>
      )}
      <div className={`pos-kiosk pos-mobile-${mobileTab}`} style={shiftLocked ? { paddingTop: 40 } : undefined}>
        {/* Hidden receipt — always in DOM so Print button never produces a blank page */}
        <div
          id="pos-receipt-print"
          style={{
            position: "fixed", left: "-9999px", top: 0,
            width: "80mm", maxWidth: "80mm", boxSizing: "border-box",
            background: "#fff", color: "#000",
            fontFamily: "Tahoma, 'Segoe UI', Arial, sans-serif",
            fontSize: "16px", lineHeight: 1.35,
          }}
        >
          <div style={{ padding: "6px 2mm", width: "80mm", maxWidth: "80mm", boxSizing: "border-box" }}>
            {saleToPrint && (() => {
              const items = saleToPrint.items || [];
              const commPct = parseFloat(saleToPrint.percent || 0);
              const commMode = saleToPrint.mode || "percent";
              const commPerQtyAmount = parseFloat(saleToPrint.perQtyAmount || 0);
              const taxed = saleToPrint.taxed !== false;

              const num = (v) => {
                const n = Number(v);
                if (!Number.isFinite(n)) return "—";
                return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
              };
              const money = (v) => {
                const n = Number(v);
                if (!Number.isFinite(n)) return "—";
                return formatCurrency(n, mainCurrency);
              };
              const cell = {
                border: "2.5px solid #000",
                borderRadius: 6,
                padding: "10px 8px",
                fontSize: 16,
                width: "50%",
                wordBreak: "break-word",
                verticalAlign: "middle",
              };
              const rowsTable = (rows, key) => (
                <table key={key} style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 6px", direction: "ltr", tableLayout: "fixed" }}>
                  <tbody>
                    {rows.map(([value, label], i) => (
                      <tr key={i}>
                        <td style={{ ...cell, fontWeight: 800, fontFamily: "Tahoma, 'Courier New', monospace", textAlign: "center", direction: "ltr", fontSize: 17 }}>{value}</td>
                        <td style={{ ...cell, fontWeight: 900, textAlign: "center", fontSize: 16 }}>{label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );

              const dateStr = (() => {
                const d = saleToPrint.date instanceof Date ? saleToPrint.date : new Date(saleToPrint.date);
                if (Number.isNaN(d.getTime())) return String(saleToPrint.date || "");
                const pad = (n) => String(n).padStart(2, "0");
                let h = d.getHours();
                const ampm = h >= 12 ? "PM" : "AM";
                h = h % 12 || 12;
                return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(h)}:${pad(d.getMinutes())} ${ampm}`;
              })();

              const receiptHeader = (
                <>
                  {headerLogoSrc ? (
                    <div style={{ textAlign: "center" }}>
                      <img src={headerLogoSrc} alt="" style={{ width: 78, height: 78, objectFit: "contain", display: "block", margin: "0 auto 6px" }} />
                    </div>
                  ) : null}
                  {companyTitle ? (
                    <div style={{ textAlign: "center", fontSize: 22, fontWeight: 900, margin: "2px 0 6px", lineHeight: 1.2 }}>{companyTitle}</div>
                  ) : null}
                  {tagline && <div style={{ textAlign: "center", fontSize: 14, fontWeight: 700, color: "#222", marginBottom: 8 }}>{tagline}</div>}
                  {phoneLines.length > 0 && (
                    <div style={{ marginBottom: 10, textAlign: "left", direction: "ltr" }}>
                      <div style={{ fontWeight: 900, marginBottom: 4, fontSize: 16 }}>
                        {t("posReceiptManagers", "Managers")}:
                      </div>
                      {phoneLines.map((l, i) => (
                        <div key={i} style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.45 }}>{l}</div>
                      ))}
                    </div>
                  )}
                </>
              );

              // One full 80mm thermal page per cart line (market slip style)
              return items.map((line, idx) => {
                const w = parseFloat(line.weight) || 0;
                const q = parseFloat(line.qty) || 0;
                const farmerName = vendorNameById[line.vendorId] || "—";
                const lineSub = line.subtotal != null
                  ? (parseFloat(line.subtotal) || 0)
                  : ((line.price || 0) * (w > 0 ? w : q));
                const lineCommPctPart = taxed && (commMode === "percent" || commMode === "mix")
                  ? (lineSub * commPct) / 100
                  : 0;
                const lineCommPerQtyPart = taxed && (commMode === "per_qty" || commMode === "mix")
                  ? q * commPerQtyAmount
                  : 0;
                const lineComm = lineCommPctPart + lineCommPerQtyPart;
                // Per-slip total is the payable amount — round only this final figure.
                const lineTotal = roundToIqdDenomination(lineSub + lineComm);
                const slipRows = [
                  [w > 0 ? num(w) : "—", t("weight", "Weight")],
                  [line.price != null && line.price !== "" ? money(line.price) : "—", t("price", "Price")],
                  [q > 0 ? num(q) : "—", t("quantity", "Quantity")],
                  [saleToPrint.customerLabel || "—", t("customer", "Customer")],
                  [farmerName, t("posPurchaseVendor", "Farmer")],
                  [line.name || "—", t("product", "Product")],
                  [money(lineSub), t("subtotal", "Subtotal")],
                  ...(lineComm > 0 ? [[money(lineComm), t("posTerminalCommission", "Commission")]] : []),
                  [money(lineTotal), t("total", "Total")],
                ];
                const isLast = idx === items.length - 1;
                return (
                  <div
                    key={line.id ?? idx}
                    className="pos-receipt-page"
                    style={{
                      width: "80mm",
                      maxWidth: "80mm",
                      boxSizing: "border-box",
                      pageBreakAfter: isLast ? "auto" : "always",
                      breakAfter: isLast ? "auto" : "page",
                      paddingBottom: isLast ? 4 : 10,
                    }}
                  >
                    {receiptHeader}
                    <div style={{ fontWeight: 900, marginBottom: 10, textAlign: "left", direction: "ltr", fontSize: 16 }}>
                      {dateStr}
                    </div>
                    {rowsTable(slipRows, `slip-${idx}`)}

                    {addressText && (
                      <div style={{ textAlign: "center", marginTop: 12, fontSize: 14, fontWeight: 800, lineHeight: 1.45 }}>
                        {addressText}
                      </div>
                    )}

                    {footerNote && (
                      <div style={{ textAlign: "center", marginTop: 10, fontSize: 14, fontWeight: 800, lineHeight: 1.45, whiteSpace: "pre-line" }}>
                        {footerNote}
                      </div>
                    )}

                    <div style={{ marginTop: 22, marginBottom: 6, textAlign: "left", direction: "ltr" }}>
                      <div style={{ borderBottom: "2.5px solid #000", width: "70%", marginBottom: 6 }} />
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                        <div style={{ fontWeight: 900, fontSize: 16 }}>{userName}</div>
                        <div style={{ fontWeight: 700, fontSize: 11 }}>
                          {saleToPrint.paymentMethod === "credit" ? t("posTerminalCredit", "Credit") : t("posTerminalCash", "Cash")}
                        </div>
                      </div>
                    </div>

                    {(displayWeb || displayEmail) && (
                      <div style={{ textAlign: "center", marginTop: 14, paddingTop: 8, borderTop: "2px dashed #333" }}>
                        {displayWeb && <div style={{ fontSize: 13, color: "#222", fontWeight: 700 }}>{displayWeb}</div>}
                        {displayEmail && <div style={{ fontSize: 13, color: "#333", fontWeight: 700 }}>{displayEmail}</div>}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* LEFT PANEL */}
        <div className="pos-left-panel">
          <div className="pos-left-topbar">
            <div className="pos-topbar-nav" style={{ position: "relative" }}>
              {[
                {
                  key: "orders",
                  icon: <UnorderedListOutlined />,
                  label: t("posNavOrders", "Orders"),
                  action: () => setSubView("orders"),
                  badge: ordersCount + offlinePendingCount,
                },
                draftsEnabled && { key: "drafts", icon: <FileTextOutlined />, label: t("posNavDrafts", "Drafts"), action: () => setSubView("drafts"), cls: "pos-nav-drafts", badge: draftsCount },
                canReturn && { key: "returns", icon: <RollbackOutlined />, label: t("posNavReturns", "Returns"), action: () => setSubView("returns") },
              ].filter(Boolean).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`pos-topbar-nav-btn${item.cls ? ` ${item.cls}` : ""}`}
                  onClick={item.action}
                  title={item.label}
                >
                  <span className="pos-nav-icon">{item.icon}</span>
                  <span className="pos-nav-label">{item.label}</span>
                  {item.badge > 0 && <span className="pos-nav-badge">{item.badge > 99 ? "99+" : item.badge}</span>}
                </button>
              ))}
              <button
                type="button"
                className={`pos-topbar-nav-btn${!isOnline || offlinePendingCount > 0 ? " pos-nav-offline" : ""}`}
                onClick={() => {
                  if (isOnline && offlinePendingCount > 0) runSync({ silent: false });
                }}
                title={
                  !isOnline
                    ? t("posOfflineMode", "Offline — sales are saved on this device")
                    : offlinePendingCount > 0
                    ? t("posOfflinePendingSync", "{{count}} sale(s) waiting to sync — tap to retry", { count: offlinePendingCount })
                    : t("posOnlineMode", "Online")
                }
              >
                <span className="pos-nav-icon">
                  {!isOnline ? <DisconnectOutlined /> : offlinePendingCount > 0 ? <CloudSyncOutlined /> : <WifiOutlined />}
                </span>
                <span className="pos-nav-label">
                  {!isOnline
                    ? t("posOfflineShort", "Offline")
                    : offlinePendingCount > 0
                    ? t("posSyncShort", "Sync")
                    : t("posOnlineShort", "Online")}
                </span>
                {offlinePendingCount > 0 && (
                  <span className="pos-nav-badge pos-nav-badge-warn">
                    {offlinePendingCount > 99 ? "99+" : offlinePendingCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                className="pos-topbar-nav-btn"
                onClick={() => {
                  setShowSettingsPanel(false);
                  setShowSessionPanel(true);
                }}
                title={t("posMySafe", "My Safe")}
              >
                <span className="pos-nav-icon"><WalletOutlined /></span>
                <span className="pos-nav-label">{t("posMySafe", "My Safe")}</span>
              </button>
              <button
                type="button"
                className="pos-topbar-nav-btn"
                onClick={() => { setShowSettingsPanel((v) => !v); setShowSessionPanel(false); }}
                title={t("settings", "Settings")}
              >
                <span className="pos-nav-icon"><UserOutlined /></span>
                <span className="pos-nav-label">{userName}</span>
              </button>
            </div>

            <div className="pos-mobile-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mobileTab === "products"}
                className={`pos-mobile-tab${mobileTab === "products" ? " active" : ""}`}
                onClick={() => setMobileTab("products")}
              >
                {t("posMobileProducts", "Products")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mobileTab === "cart"}
                className={`pos-mobile-tab${mobileTab === "cart" ? " active" : ""}`}
                onClick={() => setMobileTab("cart")}
              >
                {t("posMobileCart", "Cart")}
                {cartItemCount > 0 && (
                  <span className="pos-mobile-tab-badge">{cartItemCount > 99 ? "99+" : cartItemCount}</span>
                )}
              </button>
            </div>

            {/* Settings panel */}
            {showSettingsPanel && <div onClick={() => setShowSettingsPanel(false)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />}
            {showSettingsPanel && (
              <div style={{
                position: "fixed", top: 64, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
                background: pt.bg, border: `1px solid ${pt.border}`, borderRadius: 16,
                boxShadow: darkMode ? "0 18px 50px rgba(0,0,0,0.6)" : "0 16px 48px rgba(15,23,42,0.16)",
                padding: "18px 20px", minWidth: 250, color: pt.text,
              }}>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 14, display: "flex", alignItems: "center", gap: 8, color: pt.text }}>
                  <UserOutlined style={{ color: pt.accent }} />{userName}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: pt.faint, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>{t("language", "Language")}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {[{ v: "en", l: "EN" }, { v: "ku", l: "کوردی" }, { v: "ar", l: "عربي" }, { v: "tr", l: "TR" }].map(({ v, l }) => (
                      <button key={v} type="button" onClick={() => handleLangChange(v)} style={{
                        padding: "5px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: 600,
                        border: posLang === v ? `1.5px solid ${pt.accent}` : `1px solid ${pt.border}`,
                        background: posLang === v ? pt.accentSoft : pt.raised, color: posLang === v ? pt.accent : pt.mute,
                      }}>{l}</button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setDarkMode((d) => !d)}
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 9, marginBottom: 8,
                    border: `1px solid ${pt.border}`, background: pt.raised,
                    color: pt.text, fontWeight: 600, fontSize: 13,
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  {darkMode ? <SunOutlined style={{ color: pt.amber }} /> : <MoonOutlined style={{ color: pt.mute }} />}
                  {darkMode ? (t("lightMode", "Light Mode")) : (t("darkMode", "Dark Mode"))}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSettingsPanel(false);
                    setShowSessionPanel(true);
                  }}
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 9, marginBottom: 8,
                    border: `1px solid ${pt.border}`, background: pt.raised,
                    color: pt.text, fontWeight: 600, fontSize: 13, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  <UserOutlined style={{ color: pt.accent }} /> {t("myRegister", "My Register")}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowSettingsPanel(false); navigate("/orders"); }}
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 9,
                    border: `1px solid ${pt.dangerSoft}`, background: pt.dangerSoft,
                    color: pt.danger, fontWeight: 700, fontSize: 13, cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  <PoweroffOutlined /> {t("exitPos", "Exit POS")}
                </button>
              </div>
            )}

            {/* Session info panel — fixed so it escapes overflow:hidden on parent */}
            {showSessionPanel && <div onClick={() => setShowSessionPanel(false)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />}
            {showSessionPanel && (() => {
              const byUser = sessionSales?.by_user || [];
              // With permission, the safe shows every cashier's totals combined —
              // otherwise only the logged-in user's own row (backend already scopes
              // `by_user` the same way, this just decides how we roll it up here).
              const myStats = canSeeAllRegisterData
                ? (byUser.length
                  ? byUser.reduce((acc, u) => ({
                    sale_count: (acc.sale_count || 0) + (Number(u.sale_count) || 0),
                    total: (acc.total || 0) + (parseFloat(u.total) || 0),
                    cash_total: (acc.cash_total || 0) + (parseFloat(u.cash_total) || 0),
                    credit_total: (acc.credit_total || 0) + (parseFloat(u.credit_total) || 0),
                    cancelled: (acc.cancelled || 0) + (parseFloat(u.cancelled) || 0),
                    cancel_count: (acc.cancel_count || 0) + (Number(u.cancel_count) || 0),
                    refunds: (acc.refunds || 0) + (parseFloat(u.refunds) || 0),
                  }), {})
                  : null)
                : byUser.find((u) => String(u.user_id) === String(user.id));
              const myOrders = canSeeAllRegisterData
                ? (sessionSales?.orders || [])
                : (sessionSales?.orders || []).filter(
                  (o) => String(o.cashier_id) === String(user.id) || o.cashier === userName
                );
              return (
                <div style={{
                  position: "fixed", top: 64, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
                  background: pt.bg, border: `1px solid ${pt.border}`, borderRadius: 16,
                  boxShadow: darkMode ? "0 18px 50px rgba(0,0,0,0.6)" : "0 16px 48px rgba(15,23,42,0.16)",
                  padding: "18px 22px", minWidth: 290, color: pt.text,
                }}>
                  <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 12, display: "flex", alignItems: "center", gap: 8, color: pt.text }}>
                    <UserOutlined style={{ color: pt.accent }} />
                    {userName}
                    <span style={{
                      marginInlineStart: "auto", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20,
                      background: registerOpen ? pt.greenSoft : pt.dangerSoft, color: registerOpen ? pt.green : pt.danger,
                    }}>
                      {registerOpen ? (t("registerOpen", "Register Open")) : (t("registerClosed", "Register Closed"))}
                    </span>
                  </div>
                  {register?.id && (
                    <div style={{ fontSize: 12, color: pt.mute, marginBottom: 6, fontWeight: 600 }}>
                      {t("registerNo", "Register")} #{register.id}
                    </div>
                  )}
                  {registerOpen ? (
                    <div style={{ fontSize: 13, color: pt.mute, marginBottom: 6 }}>
                      <span style={{ fontWeight: 600 }}>{t("registerOpened", "Opened")}:</span>{" "}
                      {new Date(sessionStart).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      {" — "}
                      {new Date(sessionStart).toLocaleDateString()}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: pt.mute, marginBottom: 6 }}>
                      {t("registerClosedHint", "Your register is closed. Open it to start a new session.")}
                    </div>
                  )}
                  {sessionSales === null ? (
                    <div style={{ fontSize: 12, color: pt.faint, marginTop: 8 }}>{t("loading", "Loading...")}</div>
                  ) : myStats ? (() => {
                    const cancelledAmt = parseFloat(myStats.cancelled) || 0;
                    const refundsAmt = parseFloat(myStats.refunds) || 0;
                    // Cancel cash-backs are included in refunds — show product returns only as the leftover.
                    const returnsAmt = Math.max(0, refundsAmt - cancelledAmt);
                    const cancelCount = Number(myStats.cancel_count) || 0;
                    const totalOrder = (parseFloat(myStats.cash_total) || 0) + (parseFloat(myStats.credit_total) || 0);
                    return (
                    <div style={{ marginTop: 12, borderTop: `1px solid ${pt.border}`, paddingTop: 12 }}>
                      {canSeeAllRegisterData && (
                        <div style={{ fontSize: 10, color: pt.mute, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                          {t("posSafeAllCashiers", "All cashiers — today")}
                        </div>
                      )}
                      <div style={{
                        textAlign: "center", padding: "10px 12px", borderRadius: 10, marginBottom: 8,
                        background: pt.raised, border: `1px solid ${pt.border}`,
                      }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: pt.text, lineHeight: 1.2 }}>
                          {formatCurrency(totalOrder, mainCurrency)}
                        </div>
                        <div style={{ fontSize: 11, color: pt.faint, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 }}>
                          {t("posTotalOrder", "Total Order")}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                        <div style={{ flex: 1, background: pt.amberSoft, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: pt.amber }}>{formatCurrency(myStats.credit_total, mainCurrency)}</div>
                          <div style={{ fontSize: 10, color: pt.amber, fontWeight: 700, textTransform: "uppercase", marginTop: 2 }}>{t("debt", "Debt")}</div>
                        </div>
                        <div style={{ flex: 1, background: pt.greenSoft, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: pt.green }}>{formatCurrency(myStats.cash_total, mainCurrency)}</div>
                          <div style={{ fontSize: 10, color: pt.green, fontWeight: 700, textTransform: "uppercase", marginTop: 2 }}>{t("cash", "Cash")}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                        <div style={{ flex: 1, background: pt.dangerSoft, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: pt.danger }}>{formatCurrency(returnsAmt, mainCurrency)}</div>
                          <div style={{ fontSize: 10, color: pt.danger, fontWeight: 700, textTransform: "uppercase", marginTop: 2 }}>{t("posOrdersReturned", "Returns")}</div>
                        </div>
                        <div style={{ flex: 1, background: pt.dangerSoft, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: pt.danger }}>{formatCurrency(cancelledAmt, mainCurrency)}</div>
                          <div style={{ fontSize: 10, color: pt.danger, fontWeight: 700, textTransform: "uppercase", marginTop: 2 }}>
                            {t("statusCancelled", "Cancelled")}{cancelCount > 0 ? ` (${cancelCount})` : ""}
                          </div>
                        </div>
                      </div>
                      {myOrders.length > 0 && (
                        <div style={{ marginTop: 10, borderTop: `1px solid ${pt.border}`, paddingTop: 8 }}>
                          <div style={{ fontSize: 11, color: pt.faint, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>{t("recent", "Recent")}</div>
                          {myOrders.slice(-5).reverse().map((o) => {
                            const isCancelled = o.status === "cancelled";
                            return (
                              <div key={o.id} style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
                                fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${pt.border}`,
                                opacity: isCancelled ? 0.85 : 1,
                              }}>
                                <span style={{ color: pt.mute, fontFamily: "monospace", fontSize: 11, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                  {orderDocNo(o)} · {o.payment_method === "credit" ? t("posTerminalCredit", "Credit") : t("posTerminalCash", "Cash")}
                                  {isCancelled && (
                                    <span style={{
                                      fontFamily: "inherit", fontSize: 10, fontWeight: 800, padding: "1px 6px",
                                      borderRadius: 999, background: pt.dangerSoft, color: pt.danger,
                                      textTransform: "uppercase", letterSpacing: "0.04em",
                                    }}>
                                      {t("statusCancelled", "Cancelled")}
                                    </span>
                                  )}
                                </span>
                                <span style={{
                                  fontWeight: 700,
                                  color: isCancelled ? pt.danger : pt.text,
                                  textDecoration: isCancelled ? "line-through" : "none",
                                }}>
                                  {formatCurrency(o.total_price, mainCurrency)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    );
                  })() : (
                    <div style={{ marginTop: 12, borderTop: `1px solid ${pt.border}`, paddingTop: 12, fontSize: 13, color: pt.faint, textAlign: "center" }}>
                      {t("noSalesToday", "No sales today")}
                    </div>
                  )}
                  <div style={{ marginTop: 12, fontSize: 11, color: pt.faint, textAlign: "center" }}>
                    {t("registerAutoHint", "Your register opens automatically at login and closes at logout.")}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={() => setShowSessionPanel(false)}
                      style={{
                        flex: 1, padding: "8px 0", borderRadius: 9,
                        border: `1px solid ${pt.border}`, background: pt.raised, cursor: "pointer",
                        fontSize: 13, color: pt.mute, fontWeight: 600,
                      }}
                    >
                      {t("posDismiss", "Dismiss")}
                    </button>
                  </div>
                </div>
              );
            })()}
            <div className="pos-topbar-search-row">
              <Input
                prefix={<SearchOutlined />}
                placeholder={t("posTerminalSearchPlaceholder", "Search products...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                className="pos-topbar-search"
              />
            </div>
          </div>

          <div className="pos-product-grid">
            {productsLoading || allowedProductIds === null ? (
              <div className="pos-empty-state">{t("loading", "Loading...")}</div>
            ) : filteredProducts.length === 0 ? (
              <div className="pos-empty-state">{t("posNoProductsFound", "No products found")}</div>
            ) : (
              filteredProducts.map((product) => (
                <button
                  type="button"
                  key={product.id}
                  className="pos-product-card"
                  onClick={() => addToCart(product)}
                >
                  <div className="pos-product-img-wrap">
                    {product.image ? (
                      <img src={getStorageUrl(product.image)} alt="" className="pos-product-img" />
                    ) : (
                      <div className="pos-product-img-placeholder">
                        <ShopOutlined />
                      </div>
                    )}
                  </div>
                  <div className="pos-product-info">
                    <div className="pos-product-name">{product.name}</div>
                    <div className="pos-product-price">
                      {formatCurrency(product.price, mainCurrency)}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

        </div>

        {/* RIGHT PANEL */}
        <div className="pos-right-panel">
          <div className="pos-customer-row">
            {canCustomers && (
              <>
                <span className="pos-customer-label">
                  {t("customer", "Customer")}
                </span>
                <Select
                  showSearch
                  allowClear
                  placeholder={t("posTerminalWalkIn", "Walk-in customer")}
                  className={`pos-customer-select${validationErrors.customer ? " pos-field-error" : ""}`}
                  options={customerOptions}
                  value={selectedCustomer}
                  onChange={(v) => {
                    setSelectedCustomer(v);
                    if (v) setValidationErrors((e) => { const n = { ...e }; delete n.customer; return n; });
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  classNames={{ popup: { root: "pos-dropdown-popup" } }}
                  getPopupContainer={() => document.body}
                />
              </>
            )}
            {canQuickAdd && (
              <button
                type="button"
                className="pos-add-customer-btn"
                onClick={() => openQuickAdd(canAddCustomer ? "customer" : "farmer")}
                title={t("posQuickAdd", "Quick add customer or farmer")}
              >
                <PlusOutlined />
              </button>
            )}
            <div className="pos-toggles-group">
              <div className="pos-payment-toggle">
                <button
                  type="button"
                  className={`pos-toggle-btn${paymentMethod === "cash" ? " active" : ""}`}
                  onClick={() => setPaymentMethod("cash")}
                >
                  {t("posTerminalCash", "Cash")}
                </button>
                {canCustomers && (
                  <button
                    type="button"
                    className={`pos-toggle-btn${paymentMethod === "credit" ? " active" : ""}`}
                    onClick={() => setPaymentMethod("credit")}
                  >
                    {t("posTerminalCredit", "Credit")}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* CART — scrollable middle section */}
          <div className="pos-cart-section">
            <div className="pos-cart-table-wrap">
              <table className="pos-cart-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "start" }}>{t("posMgmtProductsName", "Name")} / {t("posPurchaseVendor", "Vendor")}</th>
                    <th style={{ textAlign: "center" }}>{t("weight", "Weight")}</th>
                    <th style={{ textAlign: "center" }}>{t("posMgmtProductsPrice", "Price")}</th>
                    <th style={{ textAlign: "center" }}>{t("posPurchaseQty", "Qty")}</th>
                    <th style={{ textAlign: "center" }}>{t("subtotal", "Subtotal")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {cart.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="pos-cart-empty-cell">
                        {t("posTerminalCartEmpty", "Cart is empty")}
                      </td>
                    </tr>
                  ) : (
                    cart.map((line) => (
                      <tr key={line.id} className="pos-cart-row">
                        <td style={{ maxWidth: 0 }}>
                          <div className="pos-cart-product-name">{line.name}</div>
                          <Select
                            size="small"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder={t("posTerminalVendorPlaceholder", "Farmer / Vendor")}
                            options={vendorOptions}
                            value={line.vendorId}
                            onChange={(v) => {
                              setVendorForLine(line.id, v);
                              if (v) setValidationErrors((e) => { const n = { ...e }; delete n[`vendor_${line.id}`]; return n; });
                            }}
                            className={`pos-vendor-select${validationErrors[`vendor_${line.id}`] ? " pos-field-error" : ""}`}
                            classNames={{ popup: { root: "pos-dropdown-popup" } }}
                            getPopupContainer={() => document.body}
                          />
                        </td>
                        <td className="pos-cart-qty-cell">
                          <InputNumber
                            className="pos-qty-input"
                            min={0}
                            value={line.weight || null}
                            onChange={(v) => setWeightForLine(line.id, v)}
                            controls={false}
                            inputMode="decimal"
                            keyboard={false}
                            placeholder={t("kg", "KG")}
                            style={{ width: "100%", background: line.weight > 0 ? "#f0fdf4" : undefined, borderColor: line.weight > 0 ? "#16a34a" : undefined }}
                          />
                        </td>
                        <td className="pos-cart-price-cell">
                          <InputNumber
                            className="pos-price-input"
                            min={0}
                            value={line.price}
                            onChange={(v) => setPriceForLine(line.id, v)}
                            controls={false}
                            inputMode="decimal"
                            keyboard={false}
                          />
                        </td>
                        <td className="pos-cart-qty-cell">
                          <InputNumber
                            className="pos-qty-input"
                            min={0}
                            value={line.qty || null}
                            onChange={(v) => setQtyDirect(line.id, v)}
                            controls={false}
                            inputMode="decimal"
                            keyboard={false}
                            placeholder={t("qty", "qty")}
                            style={{ width: "100%", background: line.qty > 0 ? "#eff6ff" : undefined, borderColor: line.qty > 0 ? "#2563eb" : undefined }}
                          />
                        </td>
                        <td className="pos-cart-total">
                          {formatCurrency(lineSubtotal(line), mainCurrency)}
                        </td>
                        <td className="pos-cart-delete-cell">
                          <button
                            type="button"
                            className="pos-cart-delete"
                            onClick={() => removeLine(line.id)}
                          >
                            <DeleteOutlined />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="pos-cart-cards">
              {cart.length === 0 ? (
                <div className="pos-cart-cards-empty">{t("posTerminalCartEmpty", "Cart is empty")}</div>
              ) : (
                cart.map((line) => (
                  <div key={line.id} className="pos-cart-card">
                    <div className="pos-cart-card-head">
                      <div className="pos-cart-product-name">{line.name}</div>
                      <button
                        type="button"
                        className="pos-cart-delete"
                        onClick={() => removeLine(line.id)}
                        aria-label={t("delete", "Delete")}
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                    <Select
                      size="large"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={t("posTerminalVendorPlaceholder", "Farmer / Vendor")}
                      options={vendorOptions}
                      value={line.vendorId}
                      onChange={(v) => {
                        setVendorForLine(line.id, v);
                        if (v) setValidationErrors((e) => { const n = { ...e }; delete n[`vendor_${line.id}`]; return n; });
                      }}
                      className={`pos-vendor-select${validationErrors[`vendor_${line.id}`] ? " pos-field-error" : ""}`}
                      classNames={{ popup: { root: "pos-dropdown-popup" } }}
                      getPopupContainer={() => document.body}
                    />
                    <div className="pos-cart-card-fields">
                      <label className="pos-cart-card-field">
                        <span>{t("weight", "Weight")}</span>
                        <InputNumber
                          className="pos-qty-input"
                          min={0}
                          value={line.weight || null}
                          onChange={(v) => setWeightForLine(line.id, v)}
                          controls={false}
                          inputMode="decimal"
                          keyboard={false}
                          placeholder={t("kg", "KG")}
                          style={{ width: "100%", background: line.weight > 0 ? "#f0fdf4" : undefined, borderColor: line.weight > 0 ? "#16a34a" : undefined }}
                        />
                      </label>
                      <label className="pos-cart-card-field">
                        <span>{t("posMgmtProductsPrice", "Price")}</span>
                        <InputNumber
                          className="pos-price-input"
                          min={0}
                          value={line.price}
                          onChange={(v) => setPriceForLine(line.id, v)}
                          controls={false}
                          inputMode="decimal"
                          keyboard={false}
                        />
                      </label>
                      <label className="pos-cart-card-field">
                        <span>{t("posPurchaseQty", "Qty")}</span>
                        <InputNumber
                          className="pos-qty-input"
                          min={0}
                          value={line.qty || null}
                          onChange={(v) => setQtyDirect(line.id, v)}
                          controls={false}
                          inputMode="decimal"
                          keyboard={false}
                          placeholder={t("qty", "qty")}
                          style={{ width: "100%", background: line.qty > 0 ? "#eff6ff" : undefined, borderColor: line.qty > 0 ? "#2563eb" : undefined }}
                        />
                      </label>
                    </div>
                    <div className="pos-cart-card-total">
                      <span>{t("subtotal", "Subtotal")}</span>
                      <strong>{formatCurrency(lineSubtotal(line), mainCurrency)}</strong>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>{/* end pos-cart-section */}

          <div className="pos-totals-section">
            {paymentMethod === "cash" && (
              <Input
                value={cashCustomerName}
                onChange={(event) => setCashCustomerName(event.target.value)}
                maxLength={191}
                allowClear
                placeholder={t("posCashCustomerName", "Cash customer name (optional)")}
                style={{ width: "100%", marginBottom: 10 }}
              />
            )}
            {isCustomerSale && retainPercent > 0 && (
              <div className="pos-total-row retain">
                <span>{t("posTerminalCommission", "Commission")} ({retainPercent}%)</span>
                <span className="pos-retained-val">{formatCurrency(retainedAmount, mainCurrency)}</span>
              </div>
            )}
            <div className="pos-total-row grand">
              <span>{t("posOrdersTotal", "Total")}</span>
              <span className="pos-grand-total">{formatCurrency(grandTotal, mainCurrency)}</span>
            </div>
          </div>

          <div className="pos-checkout-actions">
            <button
              type="button"
              className="pos-checkout-btn"
              disabled={cart.length === 0 || checkingOut || shiftLocked}
              onClick={confirmSale}
            >
              {checkingOut ? t("posTerminalSaving", "Saving...") : <><span style={{ fontSize: 18 }}>✓</span> {t("posTerminalCheckout", "CHECKOUT")}</>}
            </button>
            {draftsEnabled && (
              <button
                type="button"
                className="pos-draft-btn"
                onClick={handleSaveDraft}
                disabled={cart.length === 0}
                title={t("posTerminalDraft", "DRAFT")}
              >
                <FileTextOutlined /> {t("posTerminalDraft", "DRAFT")}
              </button>
            )}
            {/* Manual print next to checkout — hidden; auto-print / Orders reprint still work */}
            {/* <button
              type="button"
              className="pos-print-btn"
              onClick={handlePrint}
              title={t("posTerminalPrint", "PRINT")}
            >
              <PrinterOutlined /> {t("posTerminalPrint", "PRINT")}
            </button> */}
          </div>

        </div>

        <div className="pos-mobile-sticky-bar">
          <div className="pos-mobile-sticky-meta">
            <span className="pos-mobile-sticky-count">
              {cartItemCount} {t("posDraftsItems", "items")}
            </span>
            <span className="pos-mobile-sticky-total">{formatCurrency(grandTotal, mainCurrency)}</span>
          </div>
          <button
            type="button"
            className="pos-mobile-sticky-btn"
            disabled={mobileStickyPrimary.disabled}
            onClick={mobileStickyPrimary.onClick}
          >
            {mobileStickyPrimary.label}
          </button>
        </div>
      </div>

      <Modal
        open={quickAddOpen}
        title={t("posQuickAdd", "Quick add")}
        onCancel={() => setQuickAddOpen(false)}
        onOk={saveQuickAdd}
        okText={t("save", "Save")}
        confirmLoading={quickAddSaving}
        destroyOnHidden
        zIndex={4000}
        className="pos-sub-modal"
        rootClassName="pos-sub-modal-root"
      >
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {canAddCustomer && (
            <button
              type="button"
              className={`pos-toggle-btn${quickAddType === "customer" ? " active" : ""}`}
              style={{ flex: 1 }}
              onClick={() => setQuickAddType("customer")}
            >
              {t("posTerminalSaleCustomer", "Customer")}
            </button>
          )}
          {canAddFarmer && (
            <button
              type="button"
              className={`pos-toggle-btn${quickAddType === "farmer" ? " active" : ""}`}
              style={{ flex: 1 }}
              onClick={() => setQuickAddType("farmer")}
            >
              {t("posTerminalSaleFarmer", "Farmer")}
            </button>
          )}
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t("name", "Name")} *</div>
          <Input
            value={quickAddName}
            onChange={(e) => setQuickAddName(e.target.value)}
            placeholder={quickAddType === "farmer" ? (t("posPurchaseVendor", "Farmer name")) : (t("customer", "Customer name"))}
            autoFocus
            onPressEnter={saveQuickAdd}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t("phone", "Phone")}</div>
          <Input
            value={quickAddPhone}
            onChange={(e) => setQuickAddPhone(e.target.value)}
            placeholder={t("optional", "Optional")}
            onPressEnter={saveQuickAdd}
          />
        </div>
      </Modal>
    </>
  );
};

export default PosTerminal;
